import React, { useMemo } from 'react';

import { FiltersDatatableList, StoreDatatable } from '@europrocurement/flexy-datatable';
import {
    AchatsAppDispatch,
    getFromSpecifiedDataSourceThunks,
    MediaObject,
    MediaObjectApiObject,
    useAchatsService,
} from '@europrocurement/l2d-domain';
import { DataSource } from '@europrocurement/l2d-redux-utils';

import { MediaObjectApiPatchMediaObjectItemRequest } from '@europrocurement/l2d-domain/openApi/ApiAchats';
import { useApiRequest } from '@europrocurement/l2d-hooks';
import { useSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';
import useUnregisteredInvoicesListColumns from './hooks/useUnregisteredInvoicesListColumns';
import useCheckInTreatmentInvoice from './hooks/useCheckInTreatmentInvoice';
import { downloadContentName } from './components/ActionButtons/DownloadButton';

export type UnregisteredInvoicesListProps = {
    dataSource: DataSource<MediaObjectApiObject>;
    additionalFilters?: FiltersDatatableList;
};

const UnregisteredInvoicesList: React.FunctionComponent<UnregisteredInvoicesListProps> = function ({
    dataSource,
    additionalFilters,
}: UnregisteredInvoicesListProps) {
    const selectedDataSource = useMemo(() => dataSource.selected, [dataSource.selected]);

    const { mediaobjectDataSourcesThunks, mediaObjectApi } = useAchatsService();

    const getMediaObject = getFromSpecifiedDataSourceThunks<MediaObjectApiObject, string>(
        mediaobjectDataSourcesThunks,
        dataSource.name,
    );

    const { debutTraitementCheckModal } = useCheckInTreatmentInvoice({
        getMediaObject,
        selectedDataSource,
    });

    const { columns } = useUnregisteredInvoicesListColumns({
        getMediaObject,
        selectedDataSource,
        dataSource,
    });

    const filters: FiltersDatatableList = [
        {
            field: 'processingFacture',
            label: 'Pdf en traitement',
            type: 'boolean',
        },
    ];
    const { enqueueSnackbar } = useSnackbar();

    const { request } = useApiRequest();
    const dispatch = useDispatch<AchatsAppDispatch>();

    return dataSource ? (
        <StoreDatatable
            dataSource={dataSource}
            columns={columns}
            fetchData={getMediaObject}
            filters={[...(additionalFilters || []), ...filters]}
            localStorageKey="FlexyStoreDatatableMediaObject"
            rowsActions={[
                {
                    icon: 'fa-cloud-arrow-down',
                    label: 'Télécharger',
                    onClick: (items: Array<MediaObjectApiObject>) => items.map(downloadContentName),
                },
                {
                    icon: 'fa-trash',
                    label: 'Supprimer',
                    onClick: (items: Array<MediaObjectApiObject>) => {
                        const proms = items.map((item) => {
                            if (item.id) {
                                const requestParameters: MediaObjectApiPatchMediaObjectItemRequest =
                                    {
                                        id: item.id.toString(),
                                        mediaObject: { deleted: true },
                                    };
                                return request(
                                    mediaObjectApi.patchMediaObjectItem(requestParameters),
                                    {
                                        withToaster: false,
                                    },
                                );
                            }
                            return Promise.resolve();
                        });

                        Promise.all(proms)
                            .then(() => {
                                dispatch(getMediaObject({}));
                                enqueueSnackbar('Les factures ont été supprimées');
                            })
                            .catch(() => {
                                dispatch(getMediaObject({}));
                            });
                    },
                },
            ]}
            fetchWithoutFilter={false}
            onClickRow={(e: React.MouseEvent<HTMLElement>, item: MediaObject) => {
                debutTraitementCheckModal({
                    mediaObjectToRegister: item,
                    dataSourceName: dataSource.name,
                });
            }} // Empêche la bonne redirection vers la facture liée (même en utilisant un Link)
            onWheelClickRow={(e: React.MouseEvent<HTMLElement>, item: MediaObject) =>
                debutTraitementCheckModal({
                    mediaObjectToRegister: item,
                    openNewTab: true,
                    dataSourceName: dataSource.name,
                })
            } // Empêche la bonne redirection vers la facture liée (même en utilisant un Link)
        />
    ) : null;
};

export default UnregisteredInvoicesList;
